import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Icon from '../../basic/icons';
import { useDevice } from '../../../providers/device-provider';
import { NotificationMsg } from './notificationMsg';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

// const Notification = {
//     notificationTags : {
//         ORDER_IN_PROGRESS : 'ORDERS_IN_PROGRESS',
//         ORDER_READY_TO_PICK : 'ORDERS_READY_TO_PICK',
//         RX_EXPIRED : 'RX_EXPIRED',
//         RX_EXPIRE_SOON : 'RX_EXPIRE_SOON',
//         ACCOUNT_ERROR : 'ACCOUNT_ERROR',
//         BENEFIT_AVAIL : 'BENEFIT_AVAIL',
//         BENEFIT_AVAIL_SOON : 'BENEFIT_AVAIL_SOON'
//     },
//     messageStatus : {
//         ACTIVE : 'ACTIVE',
//         CLOSED : 'CLOSED'
//     },
//     userActions : {
//         CLOSEBLE : 'CLOSEBLE'
//     }
// }

const PersonalizedNotification = ({
  closeDialog,
  responseData,
  closeNotification,
  loadingNotification,
  notificationError,
}: {
  notificationError: string | null;
  responseData: any;
  closeDialog: (signal: boolean) => void;
  closeNotification: (id: string) => void;
  loadingNotification: boolean;
}) => {
  const { isMobile } = useDevice();
  const [isOpen, setIsOpen] = useState(true);
  const [dismissAnnouncement, setDismissAnnouncement] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pendingDismissRef = useRef<{ index?: number; summary: string } | null>(null);

  useLayoutEffect(() => {
    wrapperRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    const backgroundRegions = [
      document.querySelector<HTMLElement>('main#landing-page'),
      document.querySelector<HTMLElement>('aside[role="complementary"]'),
      document.querySelector<HTMLElement>('footer'),
    ].filter((element): element is HTMLElement => Boolean(element));

    const previousAriaHidden = backgroundRegions.map(element => ({
      element,
      ariaHidden: element.getAttribute('aria-hidden'),
    }));

    previousAriaHidden.forEach(({ element }) => {
      element.setAttribute('aria-hidden', 'true');
    });

    return () => {
      previousAriaHidden.forEach(({ element, ariaHidden }) => {
        if (ariaHidden === null) {
          element.removeAttribute('aria-hidden');
          return;
        }

        element.setAttribute('aria-hidden', ariaHidden);
      });
    };
  }, []);

  const handleDismiss = ({ index, summary }: { messageId: string; index?: number; summary: string }) => {
    pendingDismissRef.current = { index, summary };
  };

  useEffect(() => {
    const pending = pendingDismissRef.current;
    if (!pending) return;
    pendingDismissRef.current = null;

    setDismissAnnouncement(`${pending.summary || 'Notification'} notification removed.`);

    const newLength = responseData?.length ?? 0;
    const items = wrapperRef.current?.querySelectorAll<HTMLElement>('[data-testid="personalMsg"]');

    if (newLength === 0 || !items || items.length === 0) {
      wrapperRef.current?.focus();
      return;
    }

    const dismissedIndex = (pending.index ?? 1) - 1;
    const targetIndex = Math.min(dismissedIndex, items.length - 1);
    items[targetIndex]?.focus();
  }, [responseData]);

  const trapFocus = (event: any) => {
    if (!wrapperRef.current) return;

    const focusableElements = wrapperRef.current.querySelectorAll(
      'button, a, input, textarea, select, [tabindex]:not([tabindex="-1"])',
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    // Tab key handling: Prevent focus from leaving the dialog
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          (lastFocusableElement as HTMLElement).focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          (firstFocusableElement as HTMLElement).focus();
          event.preventDefault();
        }
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', trapFocus);
    } else {
      document.removeEventListener('keydown', trapFocus);
    }
    return () => {
      document.removeEventListener('keydown', trapFocus);
    };
  }, [isOpen]);

  if (!responseData) return;
  const notificationMsgError = notificationError || 'Error coming up while loading notifications';
  const notificationCount = responseData.length;
  const dialogLabel = `Notifications dialog. ${notificationCount} notification${notificationCount === 1 ? '' : 's'} available.`;

  return (
    <div
      className={`${isMobile ? 'z-50 fixed w-full inset-x-0 top-0' : 'z-10 w-[450px] inset-0'} bg-white rounded-lg h-auto border-gray-300 border-[1px]`}
      ref={wrapperRef}
      data-testid="notification"
      role="dialog"
      aria-modal="true"
      aria-label={dialogLabel}
      tabIndex={-1}
    >
      <div className="sr-only" role="status" aria-live="assertive">
        {dismissAnnouncement}
      </div>
      <div className="flex flex-row justify-between px-base mt-2">
        <h2 className="text-secondary-400 font-medium text-xl mt-4">
          Notifications
        </h2>
        <button
          ref={buttonRef}
          className="text-secondary-300 font-medium"
          onClick={() => {
            closeDialog(true);
            setIsOpen(false);
          }}
          data-testid="bell-button"
          aria-label="Close Notification Dialog"
        >
          <Icon name="close" className="text-2xl font-thin ml-3 mt-2" data-testid="bell-icon" />
          <span>Close</span>
        </button>
      </div>
      <div
        className={
          isMobile
            ? 'm-auto mt-6 h-auto w-full max-h-[calc(100dvh-5rem)] overflow-y-auto'
            : 'm-auto mt-6 h-auto w-full max-h-24 xxs:max-h-48 sm:max-h-88 md:max-h-96 overflow-auto'
        }
      >
        {loadingNotification ? (
          <IdleNotification text={'Fetching your updates...'} />
        ) : notificationError ? (
          <div className="text-center text-sm h-10 mb-5 p-5 flex justify-center items-center">
            {parse(notificationMsgError)}
          </div>
        ) : responseData.length === 0 ? (
          <IdleNotification text={"You're all caught up! Check back later for updates."} />
        ) : (
          responseData.length > 0 &&
          responseData.map((item: any, index: number) => {
            return (
              <div key={index}>
                {index !== 0 && (
                  <div className="flex justify-center w-full">
                    <hr className="w-[90%] border-t-2 border-gray" />
                  </div>
                )}
                <NotificationMsg
                  data={item}
                  closeNotification={closeNotification}
                  index={index + 1}
                  total={notificationCount}
                  onDismiss={handleDismiss}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
const IdleNotification = ({ text }: { text: string }) => {
  return (
    <div className="flex justify-center w-full mb-5 mt-3 text-sm">
      <p>{text}</p>
    </div>
  );
};
export default PersonalizedNotification;
